import { useRef, useState } from "react";
import type { LabelMeta, OpeningMeta, SlotShape } from "../../lib/frameDetect";
import { FramePreview } from "./FramePreview";

export type Sel = { kind: "opening" | "label"; id: string } | null;
type Box = { cx: number; cy: number; w: number; h: number };
type Dir = "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";
const HANDLES: Array<{ d: Dir; x: number; y: number; cursor: string }> = [
  { d: "nw", x: 0, y: 0, cursor: "nwse-resize" }, { d: "n", x: .5, y: 0, cursor: "ns-resize" }, { d: "ne", x: 1, y: 0, cursor: "nesw-resize" },
  { d: "e", x: 1, y: .5, cursor: "ew-resize" }, { d: "se", x: 1, y: 1, cursor: "nwse-resize" }, { d: "s", x: .5, y: 1, cursor: "ns-resize" },
  { d: "sw", x: 0, y: 1, cursor: "nesw-resize" }, { d: "w", x: 0, y: .5, cursor: "ew-resize" },
];
const MIN = 8;

/** Pure: new box after dragging a handle by (dx,dy) canvas pixels. `square` keeps w == h (circle / square shapes). Clamped to the canvas. */
export function resizeBox(b: Box, dir: Dir, dx: number, dy: number, square: boolean, cw: number, ch: number): Box {
  if (square && dir.length === 2) {
    // corner handle on a circle/square: follow the average of both drags so the shape grows/shrinks smoothly with the pointer
    const grow = ((dir.includes("e") ? dx : -dx) + (dir.includes("s") ? dy : -dy)) / 2;
    dx = dir.includes("e") ? grow : -grow;
    dy = dir.includes("s") ? grow : -grow;
  }
  let l = b.cx - b.w / 2, r = b.cx + b.w / 2, t = b.cy - b.h / 2, bt = b.cy + b.h / 2;
  if (dir.includes("w")) l += dx;
  if (dir.includes("e")) r += dx;
  if (dir.includes("n")) t += dy;
  if (dir.includes("s")) bt += dy;
  if (r - l < MIN) { if (dir.includes("w")) l = r - MIN; else r = l + MIN; }
  if (bt - t < MIN) { if (dir.includes("n")) t = bt - MIN; else bt = t + MIN; }
  if (square) {
    const size = Math.max(r - l, bt - t);
    // grow away from the edge that is NOT being dragged; edge-only handles stay centred on the other axis
    if (dir === "n" || dir === "s") { const c = (l + r) / 2; l = c - size / 2; r = c + size / 2; }
    else if (dir === "e" || dir === "w") { const c = (t + bt) / 2; t = c - size / 2; bt = c + size / 2; }
    if (dir.includes("w")) l = r - size; else if (dir.includes("e")) r = l + size;
    if (dir.includes("n")) t = bt - size; else if (dir.includes("s")) bt = t + size;
  }
  l = Math.max(0, l); t = Math.max(0, t); r = Math.min(cw, r); bt = Math.min(ch, bt);
  if (square) { const size = Math.min(r - l, bt - t); if (dir.includes("w")) l = r - size; else r = l + size; if (dir.includes("n")) t = bt - size; else bt = t + size; }
  return { cx: round((l + r) / 2), cy: round((t + bt) / 2), w: round(r - l), h: round(bt - t) };
}
export function moveBox(b: Box, dx: number, dy: number, cw: number, ch: number): Box {
  const cx = Math.min(cw - b.w / 2, Math.max(b.w / 2, b.cx + dx));
  const cy = Math.min(ch - b.h / 2, Math.max(b.h / 2, b.cy + dy));
  return { ...b, cx: round(cx), cy: round(cy) };
}
const round = (n: number) => Math.round(n * 2) / 2;   // half-pixel precision is plenty

interface Props {
  frameUrl: string;
  canvasW: number;
  canvasH: number;
  openings: OpeningMeta[];
  labels: LabelMeta[];
  dpUrls?: string[];
  stageWidth: number;
  selected: Sel;
  onSelect: (s: Sel) => void;
  onChange: (openings: OpeningMeta[], labels: LabelMeta[]) => void;
}

/** Drag a box to move it, drag a handle to resize it. Everything is stored in the frame image's own pixels. */
export function FrameEditor({ frameUrl, canvasW, canvasH, openings, labels, dpUrls, stageWidth, selected, onSelect, onChange }: Props) {
  const s = stageWidth / canvasW;
  const drag = useRef<{ kind: "opening" | "label"; id: string; dir: Dir | "move"; sx: number; sy: number; start: Box; shape?: SlotShape } | null>(null);
  const [, force] = useState(0);

  function begin(e: React.PointerEvent, kind: "opening" | "label", id: string, dir: Dir | "move") {
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const o = kind === "opening" ? openings.find((x) => x.id === id) : labels.find((x) => x.id === id);
    if (!o) return;
    drag.current = { kind, id, dir, sx: e.clientX, sy: e.clientY, start: { cx: o.cx, cy: o.cy, w: o.w, h: o.h }, shape: kind === "opening" ? (o as OpeningMeta).shape : undefined };
    onSelect({ kind, id });
    force((n) => n + 1);
  }
  function move(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / s, dy = (e.clientY - d.sy) / s;
    const square = d.shape === "circle" || d.shape === "square";
    const nb = d.dir === "move" ? moveBox(d.start, dx, dy, canvasW, canvasH) : resizeBox(d.start, d.dir, dx, dy, square, canvasW, canvasH);
    if (d.kind === "opening") onChange(openings.map((o) => (o.id === d.id ? { ...o, ...nb } : o)), labels);
    else onChange(openings, labels.map((l) => (l.id === d.id ? { ...l, ...nb } : l)));
  }
  function end() { drag.current = null; force((n) => n + 1); }

  const boxStyle = (b: Box, color: string, isSel: boolean, round?: string): React.CSSProperties => ({
    position: "absolute", left: (b.cx - b.w / 2) * s, top: (b.cy - b.h / 2) * s, width: b.w * s, height: b.h * s, boxSizing: "border-box",
    border: `${isSel ? 2 : 1}px ${isSel ? "solid" : "dashed"} ${color}`, borderRadius: round, cursor: "move", touchAction: "none",
    background: isSel ? `${color}22` : "transparent",
  });

  return (
    <div className="inline-block rounded-lg border border-surface-border" style={{ background: "repeating-conic-gradient(#8884 0% 25%, transparent 0% 50%) 50% / 16px 16px" }}
      onPointerMove={move} onPointerUp={end} onPointerCancel={end} onPointerDown={() => onSelect(null)}>
      <FramePreview meta={{ canvasW, canvasH, openings, labels }} frameUrl={frameUrl} width={stageWidth} dpUrls={dpUrls}>
        {openings.map((o) => {
          const isSel = selected?.kind === "opening" && selected.id === o.id;
          return (
            <div key={`o-${o.id}`} style={boxStyle(o, "#2dd4bf", isSel, o.shape === "circle" || o.shape === "oval" ? "50%" : o.shape === "rounded" ? "20%" : undefined)}
              onPointerDown={(e) => begin(e, "opening", o.id, "move")} title={`opening ${o.id}`}>
              <span style={{ position: "absolute", left: 2, top: 0, fontSize: 10, color: "#2dd4bf", textShadow: "0 0 2px #000" }}>{o.id}</span>
              {isSel && HANDLES.map((h) => (
                <span key={h.d} onPointerDown={(e) => begin(e, "opening", o.id, h.d)}
                  style={{ position: "absolute", left: `${h.x * 100}%`, top: `${h.y * 100}%`, width: 12, height: 12, margin: -6, background: "#fff", border: "2px solid #2dd4bf", borderRadius: 2, cursor: h.cursor, touchAction: "none" }} />
              ))}
            </div>
          );
        })}
        {labels.map((l) => {
          const isSel = selected?.kind === "label" && selected.id === l.id;
          return (
            <div key={`l-${l.id}`} style={boxStyle(l, "#f59e0b", isSel)} onPointerDown={(e) => begin(e, "label", l.id, "move")} title={`text box ${l.id}`}>
              {isSel && HANDLES.map((h) => (
                <span key={h.d} onPointerDown={(e) => begin(e, "label", l.id, h.d)}
                  style={{ position: "absolute", left: `${h.x * 100}%`, top: `${h.y * 100}%`, width: 10, height: 10, margin: -5, background: "#fff", border: "2px solid #f59e0b", borderRadius: 2, cursor: h.cursor, touchAction: "none" }} />
              ))}
            </div>
          );
        })}
      </FramePreview>
    </div>
  );
}
